import { baseApi } from '@/shared/api/baseApi.js';
import type {
  Participant,
  Conversation,
  Message,
  MessageType,
  GetMessagesParams,
  CreateConversationRequest,
  SendMessageRequest,
} from './chat.types.js';

export type {
  Participant,
  Conversation,
  Message,
  MessageType,
  GetMessagesParams,
  CreateConversationRequest,
  SendMessageRequest,
};

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<{ success: true; data: Conversation[] }, void>({
      query: () => '/chat/conversations',
      providesTags: ['Conversations'],
      // Keep cached for 5 min after last subscriber unmounts — prevents re-fetch
      // spam when navigating chat → other page → chat.
      keepUnusedDataFor: 300,
    }),

    getConversation: builder.query<{ success: true; data: Conversation }, string>({
      query: (id) => `/chat/conversations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Conversations', id }],
      keepUnusedDataFor: 300,
    }),

    createConversation: builder.mutation<
      { success: true; data: Conversation },
      CreateConversationRequest
    >({
      query: (body) => ({ url: '/chat/conversations', method: 'POST', body }),
      invalidatesTags: ['Conversations'],
    }),

    getMessages: builder.query<
      { success: true; data: { messages: Message[]; nextCursor?: string } },
      GetMessagesParams
    >({
      query: ({ conversationId, cursor, limit = 50 }) => {
        const params = new URLSearchParams();
        if (cursor) params.set('cursor', cursor);
        params.set('limit', limit.toString());
        return `/chat/conversations/${conversationId}/messages?${params.toString()}`;
      },
      providesTags: (_result, _error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
      ],
      // Keep fetched messages for 5 min — quick switch back to a chat we just left
      // should not re-fetch from server.
      keepUnusedDataFor: 300,
    }),

    sendMessage: builder.mutation<{ success: true; data: Message }, SendMessageRequest>({
      query: ({ conversationId, ...body }) => ({
        url: `/chat/conversations/${conversationId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
      ],
    }),

    uploadAttachment: builder.mutation<
      { success: true; data: Message },
      {
        conversationId: string;
        file: File;
        type: 'image' | 'video' | 'voice' | 'file';
        caption?: string;
        replyToId?: string;
        duration?: number;
      }
    >({
      query: ({ conversationId, file, type, caption, replyToId, duration }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        if (caption) formData.append('caption', caption);
        if (replyToId) formData.append('replyToId', replyToId);
        if (duration !== undefined) formData.append('duration', String(duration));
        return {
          url: `/chat/conversations/${conversationId}/attachments`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        'Conversations',
      ],
    }),

    sendSticker: builder.mutation<
      { success: true; data: Message },
      { conversationId: string; url: string; replyToId?: string }
    >({
      query: ({ conversationId, url, replyToId }) => ({
        url: `/chat/conversations/${conversationId}/sticker`,
        method: 'POST',
        body: { url, replyToId },
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        'Conversations',
      ],
    }),

    editMessage: builder.mutation<
      { success: true; data: Message },
      { messageId: string; content: string }
    >({
      query: ({ messageId, content }) => ({
        url: `/chat/messages/${messageId}`,
        method: 'PATCH',
        body: { content },
      }),
    }),

    deleteMessage: builder.mutation<void, string>({
      query: (messageId) => ({
        url: `/chat/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Messages', 'Conversations'],
    }),

    deleteConversation: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: `/chat/conversations/${conversationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Conversations'],
    }),

    markAsRead: builder.mutation<void, { conversationId: string; messageId?: string }>({
      query: ({ conversationId, messageId }) => ({
        url: `/chat/conversations/${conversationId}/read`,
        method: 'POST',
        body: messageId ? { messageId } : {},
      }),
      invalidatesTags: ['Conversations'],
    }),

    toggleMute: builder.mutation<any, string>({
      query: (conversationId) => ({
        url: `/chat/conversations/${conversationId}/mute`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationId) => [
        'Conversations',
        { type: 'Conversations', id: conversationId },
      ],
    }),

    toggleCounted: builder.mutation<any, string>({
      query: (conversationId) => ({
        url: `/chat/conversations/${conversationId}/toggle-counted`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationId) => [
        'Conversations',
        { type: 'Conversations', id: conversationId },
      ],
    }),

    getUnreadTotal: builder.query<{ success: true; data: { count: number } }, void>({
      query: () => '/chat/unread-total',
      providesTags: ['Conversations'],
    }),

    findChatByProject: builder.query<{ success: true; data: Conversation | null }, string>({
      query: (projectId) => `/chat/conversations/by-project/${projectId}`,
      providesTags: ['Conversations'],
    }),

    findChatByTeam: builder.query<{ success: true; data: Conversation | null }, string>({
      query: (teamId) => `/chat/conversations/by-team/${teamId}`,
      providesTags: ['Conversations'],
    }),

    getUsersStatus: builder.query<
      { success: true; data: Record<string, { online: boolean; lastSeen: string | null }> },
      string[]
    >({
      query: (userIds) => `/chat/users/status?${userIds.map((id) => `userId=${id}`).join('&')}`,
      providesTags: ['UserStatus'],
      // User online status — cached for 60 sec so every ChatWindow mount
      // doesn't trigger a fresh round-trip.
      keepUnusedDataFor: 60,
      // Prevent identical arrays from causing cache misses (order-independent).
      serializeQueryArgs: ({ queryArgs }) => {
        return [...queryArgs].sort().join(',');
      },
    }),

    getRecentContacts: builder.query<
      {
        success: true;
        data: Array<{
          userId: string;
          name: string;
          email: string;
          avatarUrl: string | null;
          lastChatAt: string;
        }>;
      },
      { limit?: number } | void
    >({
      query: (arg) => {
        const limit = arg && 'limit' in arg ? (arg.limit ?? 10) : 10;
        return `/chat/recent-contacts?limit=${limit}`;
      },
      providesTags: ['Conversations'],
    }),

    toggleHideStatus: builder.mutation<any, string>({
      query: (conversationId) => ({
        url: `/chat/conversations/${conversationId}/toggle-hide-status`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => ['Conversations', { type: 'Conversations', id }],
    }),

    addParticipant: builder.mutation<
      { success: true; data: any },
      {
        conversationId: string;
        userId: string;
        addToProject?: boolean;
        projectRole?: 'lead' | 'worker' | 'viewer';
      }
    >({
      query: ({ conversationId, userId, addToProject, projectRole }) => ({
        url: `/chat/conversations/${conversationId}/participants`,
        method: 'POST',
        body: {
          userId,
          ...(addToProject !== undefined && { addToProject }),
          ...(projectRole && { projectRole }),
        },
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'Conversations', id: conversationId },
        'Conversations',
        'Project',
        'Teams',
      ],
    }),

    removeParticipant: builder.mutation<
      { success: true; data: any },
      { conversationId: string; userId: string }
    >({
      query: ({ conversationId, userId }) => ({
        url: `/chat/conversations/${conversationId}/participants/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: 'Conversations', id: conversationId },
        'Conversations',
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationQuery,
  useCreateConversationMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useUploadAttachmentMutation,
  useSendStickerMutation,
  useEditMessageMutation,
  useDeleteMessageMutation,
  useDeleteConversationMutation,
  useMarkAsReadMutation,
  useToggleMuteMutation,
  useToggleCountedMutation,
  useGetUnreadTotalQuery,
  useFindChatByProjectQuery,
  useFindChatByTeamQuery,
  useGetUsersStatusQuery,
  useGetRecentContactsQuery,
  useToggleHideStatusMutation,
  useAddParticipantMutation,
  useRemoveParticipantMutation,
} = chatApi;
